import React, { PropsWithChildren, useEffect, useState } from 'react'
import { Divider as MuiDivider, InputAdornment, Stack, styled } from '@mui/material'

import { Button } from '../../../components/Button'
import { Modal } from '../../../components/Modal'
import { CartItem } from '../storage/cart.types'
import { RevertChangesButton } from '../../products/components/ProductCard/RevertChangesButton'
import Input from '../../../components/Input'
import TestIds from '../../../testIds'
import { CostPriceList } from '../../cost-price/components/CostPrice'
import { CartProductCard } from '../../products/components/ProductCard/CartProductCard'

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}))

const Divider = styled(MuiDivider)(({ theme }) => ({
  borderColor: theme.colors.primary200,
  margin: '8px 0',
}))

interface EditPriceModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  item?: CartItem
  onApplyEditedPrince: (newPrice: number, newQuantity: number, item: CartItem) => void
  onRevertEditedPrice: (item: CartItem) => void
}

export const EditPriceModal = ({
  open,
  setOpen,
  item,
  onApplyEditedPrince,
  onRevertEditedPrice,
}: PropsWithChildren<EditPriceModalProps>) => {
  const [unitPrice, setUnitPrice] = useState<string | undefined>(undefined)
  const [unitPriceError, setUnitPriceError] = useState<string | undefined>('')

  const [quantity, setQuantity] = React.useState(item?.quantity ?? 0)
  const [editedPrice, setEditedPrice] = React.useState(item?.editedPriceExcGst)
  // Ensure the unit price is for the current item.
  useEffect(() => {
    if (item) {
      setUnitPrice((item.discountedPriceExcGst / item.quantity).toFixed(2))
      setEditedPrice(item?.editedPriceExcGst)
      setQuantity(item?.quantity ?? 0)
    }
  }, [item, open])

  const handleCloseModal = () => {
    setOpen(false)
  }

  const revertChanges = () => {
    if (item) {
      setUnitPrice(undefined)
      onRevertEditedPrice(item)
    }
    handleCloseModal()
  }

  const applyEditedPrice = () => {
    if (!unitPrice) {
      setUnitPriceError('Please enter a valid unit price')
      return
    }

    const inputValue = parseFloat(unitPrice)
    if (inputValue <= 0) {
      setUnitPriceError('Please enter a unit price greater than $0')
      return
    } else {
      setUnitPriceError(undefined)
    }

    if (unitPrice && item) {
      onApplyEditedPrince(parseFloat(unitPrice), quantity, item)
    }
    handleCloseModal()
  }

  const priceList = React.useMemo(() => {
    if (item) {
      return <CostPriceList productId={item.productId} />
    }
  }, [item?.productId])

  const footer = React.useMemo(() => {
    return (
      <StyledButton
        data-testid={TestIds.feat.editPrice.saveChanges}
        variant='contained'
        onClick={applyEditedPrice}
      >
        Save changes
      </StyledButton>
    )
  }, [applyEditedPrice])

  if (!item) return null

  return (
    <Modal
      title='Edit price'
      open={open}
      handleClose={handleCloseModal}
      footer={footer}
      testId={TestIds.feat.editPrice.modal}
    >
      <Stack spacing={4} pb={2}>
        <CartProductCard
          item={{
            ...item,
            quantity: quantity,
            discountedPriceExcGst: editedPrice ?? item.discountedPriceExcGst,
          }}
          onChangeQuantity={(value) => {
            setQuantity(value)
            if (unitPrice) {
              setEditedPrice(parseFloat(unitPrice) * value)
            }
          }}
        />
        <Divider />
        {priceList}
        <Input
          label='Unit price (GST excl.):'
          placeholder={unitPrice}
          value={unitPrice}
          setValue={(v) => {
            const value = v?.valueOf().toString()
            // Allow empty input
            if (!value) {
              setUnitPrice('')
              setEditedPrice(0)
              return
            }

            // Validate the input using regex
            const regex = /^\d{0,9999}(\.\d{0,2})?$/
            if (regex.test(value) || value === '') {
              setUnitPrice(value)
              setEditedPrice(parseFloat(value) * quantity)
            }
          }}
          errorMessage={unitPriceError}
          outlineInputProps={{
            type: 'number',
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          type='number'
          testId={TestIds.feat.editPrice.unitPrice}
        />
        <RevertChangesButton onClick={revertChanges} />
      </Stack>
    </Modal>
  )
}
